import React, { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import Button from '@mui/material/Button';
import { useForm } from 'react-hook-form';
import { Link, useHistory } from 'react-router-dom';
import { TextInputController } from '../InputControllers/TextInputController';
import { AuthDialog } from '../AuthDialog/AuthDialog';
import { useTranslation } from 'react-i18next';
import { PasswordInputController } from '../InputControllers/PasswordInputController';
import Box from '@mui/material/Box';
import { EAuthResponse, useAuth } from '../../hooks/useAuth';
import { NotificationContext } from '../../contexts/NotificationContext';
import { GuestRoutes } from '../../routers/GuestRouter';
import { useQueryParam } from '../../hooks/useQueryParam';
import Alert from '@mui/material/Alert';
import { AuthContext } from '../../contexts/AuthContext';
import { authenticatedRoot } from '../../routers/AuthenticatedRoutes';
import { useHistoryWithSearch } from '../../hooks/useHistoryWithSearch';
import { useRedirectAfterAuth } from '../../hooks/useRedirectAfterAuth';
import CloseRounded from '@mui/icons-material/CloseRounded';
import InputAdornment from '@mui/material/InputAdornment';
import { IconButton } from '@mui/material';
import { queryUrl } from '../../hooks/useQueryUrl';

const requiredRule = { required: true };

export const AuthEmail: FC = () => {
  const { authenticate } = useAuth();

  const { t } = useTranslation();

  const { identity, identities } = useContext(AuthContext);

  const forceEmail = useQueryParam('email');

  const flow = useQueryParam('flow');

  const { addNotification } = useContext(NotificationContext);
  const history = useHistory();

  const { replace: replaceWithSearch } = useHistoryWithSearch();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setFocus,
  } = useForm();

  const redirectAfterAuth = useRedirectAfterAuth();

  const onSubmit = useCallback(
    async (data) => {
      const result = await authenticate(data.email, data.password);

      if (result === EAuthResponse.Success) {
        redirectAfterAuth();
      } else if (result === EAuthResponse.SecondFactorNeeded) {
        replaceWithSearch(GuestRoutes.SecondFactor());
      } else if (result === EAuthResponse.Unauthorized) {
        addNotification({
          message: t('auth.signIn.errors.unauthorized'),
          severity: 'error',
        });
      } else if (result === EAuthResponse.Unexpected) {
        addNotification({
          message: t('auth.signIn.errors.unexpected'),
          severity: 'error',
        });
      }
    },
    [redirectAfterAuth]
  );

  const onClearForceEmail = useCallback(() => {
    history.push(queryUrl('email', null, history.location.search));
  }, []);

  useEffect(() => {
    reset({
      email: forceEmail ?? '',
      password: '',
    });
  }, [forceEmail]);

  useEffect(() => {
    if (forceEmail && identity?.email === forceEmail) {
      const search = new URLSearchParams(history.location.search);
      search.delete('email');
      if (search.toString().length > 0) {
        history.replace(`${authenticatedRoot}?${search}`);
      } else {
        history.replace(authenticatedRoot);
      }
    }
  }, [forceEmail]);

  const emailInputProps = useMemo(() => {
    return {
      autoComplete: 'email',
      disabled: !!forceEmail,
      ref: (el: HTMLInputElement | null) => {
        if (el && !forceEmail) {
          setFocus('email');
        }
      },
      endAdornment: forceEmail ? (
        <InputAdornment position="end">
          <IconButton onClick={onClearForceEmail}>
            <CloseRounded />
          </IconButton>
        </InputAdornment>
      ) : (
        void 0
      ),
    };
  }, [forceEmail, onClearForceEmail]);

  const passwordInputProps = useMemo(
    () => ({
      autoComplete: 'current-password',
      ref: (el: HTMLInputElement | null) => {
        if (el && forceEmail) {
          setFocus('password');
        }
      },
    }),
    [forceEmail]
  );

  return (
    <AuthDialog
      title={t('auth.email.encouragement')}
      onSubmit={handleSubmit(onSubmit)}
      actions={[
        identities.length > 0 ? (
          <Button
            key="accounts"
            variant="text"
            color="primary"
            component={Link}
            to={`${GuestRoutes.Account()}${history.location.search}`}
          >
            {t('auth.email.accountsButton')}
          </Button>
        ) : null,
        <Button key="signIn" variant="contained" color="primary" type="submit">
          {t('auth.email.submitButton')}
        </Button>,
      ]}
    >
      {flow === 'invitation' ? (
        <Alert severity={'info'}>
          {identity ? t('invitation.alert.switchAccount', { email: identity.email }) : t('invitation.alert.signIn')}
        </Alert>
      ) : null}

      <TextInputController
        control={control}
        errors={errors}
        label={t('auth.inputs.email.label')}
        name="email"
        inputType="email"
        InputProps={emailInputProps}
        rules={requiredRule}
      />

      <PasswordInputController
        control={control}
        errors={errors}
        label={t('auth.inputs.password.label')}
        name="password"
        rules={requiredRule}
        InputProps={passwordInputProps}
      />

      <Box textAlign="right">
        <Button component={Link} to={GuestRoutes.ForgotPassword()} variant="text" color="secondary" size="small">
          {t('auth.signIn.forgotPassword')}
        </Button>
      </Box>
    </AuthDialog>
  );
};
